/**
 * Floating-point comparison.
 *
 * Every equality test in the geometry kernel goes through this module. Direct `===`
 * on computed coordinates is a bug: a point constructed as the intersection of two
 * lines is essentially never bit-identical to the same point constructed as the
 * midpoint of a segment, even when they are the same point mathematically.
 */

/**
 * Default tolerance for world-space coordinate comparisons.
 *
 * Chosen relative to double precision (~1e-16 relative) with roughly seven decimal
 * digits of headroom, so a chain of a few hundred dependent constructions still
 * compares equal. Tighter values produce false negatives after deep DAG evaluation;
 * looser values start merging genuinely distinct points in dense diagrams.
 */
export const EPSILON = 1e-9;

/** Tolerance for values already normalised to roughly unit magnitude (directions, dot products). */
export const UNIT_EPSILON = 1e-12;

/**
 * Hybrid absolute/relative comparison.
 *
 * The absolute test handles values near zero, where a relative test degenerates
 * (`|a - b| <= eps * max(|a|, |b|)` is only satisfied by exact equality when both
 * operands are 0). The relative test keeps the comparison meaningful for large
 * coordinates, where an absolute epsilon of 1e-9 would be stricter than the
 * representable precision.
 */
export function approxEqual(a: number, b: number, epsilon: number = EPSILON): boolean {
  if (a === b) return true; // fast path, and makes ±Infinity compare equal to itself

  // Beyond the fast path, only finite values can be "approximately" equal. Without
  // this guard the relative branch below reduces to `Infinity <= Infinity`, which
  // would report +Infinity and -Infinity as equal. NaN also lands here and is
  // correctly rejected.
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;

  const diff = Math.abs(a - b);
  if (diff <= epsilon) return true;
  return diff <= epsilon * Math.max(Math.abs(a), Math.abs(b));
}

/** True when `value` is indistinguishable from zero. Purely absolute — relative has no meaning at 0. */
export function approxZero(value: number, epsilon: number = EPSILON): boolean {
  return Math.abs(value) <= epsilon;
}

/** `-1`, `0` or `1`, treating anything within `epsilon` of zero as `0`. */
export function approxSign(value: number, epsilon: number = EPSILON): -1 | 0 | 1 {
  if (value > epsilon) return 1;
  if (value < -epsilon) return -1;
  return 0;
}

/** Three-way comparison with tolerance: `-1` if `a < b`, `1` if `a > b`, `0` if approximately equal. */
export function approxCompare(a: number, b: number, epsilon: number = EPSILON): -1 | 0 | 1 {
  if (approxEqual(a, b, epsilon)) return 0;
  return a < b ? -1 : 1;
}

/** True when `value` lies in `[min, max]`, tolerating `epsilon` overshoot at either end. */
export function approxBetween(
  value: number,
  min: number,
  max: number,
  epsilon: number = EPSILON
): boolean {
  return value >= min - epsilon && value <= max + epsilon;
}

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Numerically stable quadratic solver for `a·x² + b·x + c = 0`.
 *
 * Uses the Citardauq form for the root that would otherwise suffer catastrophic
 * cancellation. The textbook formula loses most of its significant digits when
 * `b² >> 4ac` — exactly the case of a line grazing a circle, which is precisely
 * where a geometry editor needs the answer to be right.
 *
 * Returns roots in ascending order: 0 roots (no real solution), 1 root (tangency,
 * within `epsilon`), or 2 roots.
 */
export function solveQuadratic(
  a: number,
  b: number,
  c: number,
  epsilon: number = EPSILON
): number[] {
  if (approxZero(a, epsilon)) {
    // Degenerates to linear: b·x + c = 0
    if (approxZero(b, epsilon)) return [];
    return [-c / b];
  }

  const discriminant = b * b - 4 * a * c;

  // Scale the tangency threshold with the magnitude of the terms, otherwise a large
  // circle far from the origin never registers as tangent.
  const scale = Math.max(Math.abs(b * b), Math.abs(4 * a * c), 1);
  if (Math.abs(discriminant) <= epsilon * scale) {
    return [-b / (2 * a)];
  }
  if (discriminant < 0) return [];

  const sqrtD = Math.sqrt(discriminant);
  // q is computed so that the two terms of b never cancel.
  const q = b >= 0 ? -0.5 * (b + sqrtD) : -0.5 * (b - sqrtD);
  const r1 = q / a;
  const r2 = c / q;
  return r1 <= r2 ? [r1, r2] : [r2, r1];
}
